<script lang="ts" setup>
import { i18n } from '@/i18n';
import TableHead from './TableHead.vue';
import ArrowIcon from './ArrowIcon.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import { TableContentProperty } from '../interface/tableContentHeaders';
import { BaseTableContentViewModel } from '../baseTableContentViewModel';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { HeaderList, TableType } from '@/coreDomainsModule/table';

const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    headerIndex: number;
    accumulationTableContentWidth: number[];
    isVariableWidth: boolean;
    stableColumnCount: number;
    isEditing?: boolean;
    isBold: boolean;
    isAllSelected: boolean;
    modifiableHeaderList?: HeaderList;
    isOpenHeaderModifyButtonMenu?: boolean;
    tableType: TableType;
  }>(),
  {
    // eslint-disable-next-line jsdoc/require-jsdoc
    modifiableHeaderList: () => [] as HeaderList,
  }
);

const baseTableContentVM = new BaseTableContentViewModel();

// emits
const emits = defineEmits<{
  (e: 'onClickSort', index: number, key: TableContentProperty, isAsc: boolean): void;
  (e: 'onMousedownResizer', event: MouseEvent, headerIndex: number): void;
  (e: 'onCheckboxSelected', displayId: string, isAllSelected: boolean): void;
  (e: 'onToggleDisplayHeader', key: string, isHide: boolean): void;
  (e: 'onCheckboxSelectedBusinessId'): void;
  (e: 'onToggleHeaderModifyButtonMenu', isOpen: boolean): void;
}>();

const t = i18n.t;
</script>

<template>
  <TableHead
    :class="[baseTableContentVM.isSticky(stableColumnCount, headerIndex) ? 'sticky-content' : '']"
    :style="{
      left: baseTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth[headerIndex]),
    }"
    :index="headerIndex"
    :resizable="header.isVariableWidth && !baseTableContentVM.isSticky(stableColumnCount, headerIndex)"
    @on-mousedown-resizer="($event) => emits('onMousedownResizer', $event, headerIndex)"
  >
    <template v-if="header.key === 'choice'">
      <RowCheckBox
        v-if="tableType === 'firmOrderTable' || !isEditing"
        :is-checked="isAllSelected"
        class="md-primary all-check-button"
        @change-status="$emit('onCheckboxSelected', '', true)"
      />
    </template>

    <template v-if="header.key === 'choiceBusinessId'">
      <RowCheckBox
        v-if="!isEditing"
        :is-checked="isAllSelected"
        class="md-primary all-check-button"
        @change-status="$emit('onCheckboxSelectedBusinessId')"
      />
    </template>

    <template v-else-if="header.key === 'expansion'">
      <!-- 展開ボタンのヘッダーは何も表示しない -->
      {{ '' }}
    </template>

    <template v-else-if="header.key === 'headerModify'">
      <TableHeaderModifyButton
        :modifiable-header-list="modifiableHeaderList"
        :is-open="isOpenHeaderModifyButtonMenu"
        @toggle="
          (key, isHide) => {
            emits('onToggleDisplayHeader', key, isHide);
          }
        "
        @toggle-menu="
          (isOpen) => {
            emits('onToggleHeaderModifyButtonMenu', isOpen);
          }
        "
      />
    </template>

    <template v-else-if="header.sortOption.isSortable">
      <div class="table-header__sortable-content">
        <div class="table-header__text">{{ header.displayLabel ?? t(header.key) }}</div>
        <ArrowIcon :asc="header.sortOption.isAsc" @on-click="() => emits('onClickSort', headerIndex, header, header.sortOption.isAsc)" />
      </div>
    </template>

    <template v-else>
      <div class="table-header__text">{{ header.displayLabel ?? t(header.key) }}</div>
    </template>
  </TableHead>
</template>
